import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type ConnectionProfile } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';
import type { ConnectionProfileRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const CONNECTION_PROFILES = 'connectionProfiles';

const converter = buildConverter<ConnectionProfile>(
  (data) =>
    ({
      ...data,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
    }) as ConnectionProfile,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreConnectionProfileRepository implements ConnectionProfileRepository {
  async findAll(): Promise<ConnectionProfile[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, CONNECTION_PROFILES),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<ConnectionProfile | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, CONNECTION_PROFILES, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<ConnectionProfile[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, CONNECTION_PROFILES),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(profile: ConnectionProfile): Promise<ConnectionProfile> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const data = { ...profile, environment: getCurrentEnvironment() };
    const existing = await this.findById(profile.id);
    await setDoc(doc(db, CONNECTION_PROFILES, profile.id).withConverter(converter), data);
    return {
      ...data,
      createdAt: existing?.createdAt ?? profile.createdAt,
      updatedAt: new Date().toISOString(),
    };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, CONNECTION_PROFILES, id));
  }

  async duplicate(id: string, newName?: string): Promise<ConnectionProfile | undefined> {
    const original = await this.findById(id);
    if (!original) return undefined;
    const duplicated: ConnectionProfile = {
      ...original,
      id: `${original.id}-copy-${Date.now()}`,
      name: newName || `${original.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await this.save(duplicated);
    return duplicated;
  }
}

export const createFirestoreConnectionProfileRepository = (): FirestoreConnectionProfileRepository =>
  new FirestoreConnectionProfileRepository();
